import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'dh-breadcrumbs-type-1';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

/**
 * Breadcrumb navigation rendering an ordered list of links with icon separators and Schema.org BreadcrumbList microdata.
 * Horizontal flex list that wraps to multiple lines on narrow viewports; text and separator icons inherit `currentColor` so the component adapts to any surrounding background.
 * Differs from sibling breadcrumb types by being visually neutral (no own background, no color-mode prop) — color, spacing context, and container come from the parent block.
 *
 * @category DocHub
 * @example
 *   <dh-breadcrumbs-type-1 items='[{"label":"Home","href":"/"},{"label":"Tax Form Catalog","href":"/tax-form-catalog"},{"label":"W9 form"}]'></dh-breadcrumbs-type-1>
 */
@Component({
  tag: 'dh-breadcrumbs-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DhBreadcrumbsType1 {
  /**
   * Breadcrumb navigation items displayed in order from root to current page. Omit `href` on the last item to render it as the current-page label.
   *
   * @type {array}
   * @item.type {object}
   * @item.property {string} label - Visible breadcrumb text (e.g. "Home", "Products")
   * @item.property {string} href - Navigation URL. Omit for the last (current page) item to render it as plain text.
   * @item.required label
   * @item.content label
   * @minItems 1
   */
  @Prop() items!: BreadcrumbItem[] | string;

  /** Icon name passed to `dh-icon` for the visual separator between items. */
  @Prop() separator: string = 'arrows--chevron-right';

  /**
   * Accessible label for the breadcrumb navigation landmark.
   * @content
   */
  @Prop() ariaLabel: string = 'Breadcrumb';

  render() {
    const items = parseJsonProp<BreadcrumbItem[]>(this.items, []) ?? [];

    if (items.length === 0) {
      return null;
    }

    return (
      <nav
        class={COMPONENT_NAME}
        aria-label={this.ariaLabel}
        itemscope
        itemtype="https://schema.org/BreadcrumbList"
      >
        <ol class={`${COMPONENT_NAME}__list`}>
          {items.map((item, index) => {
            const isCurrent = index === items.length - 1;
            const itemClass = `${COMPONENT_NAME}__item${isCurrent ? ` ${COMPONENT_NAME}__item--current` : ''}`;

            return (
              <li
                class={itemClass}
                itemprop="itemListElement"
                itemscope
                itemtype="https://schema.org/ListItem"
              >
                {item.href && !isCurrent ? (
                  <a class={`${COMPONENT_NAME}__link`} href={item.href} itemprop="item">
                    <span itemprop="name">{item.label}</span>
                  </a>
                ) : (
                  <span
                    class={`${COMPONENT_NAME}__text`}
                    aria-current={isCurrent ? 'page' : undefined}
                    itemprop="name"
                  >
                    {item.label}
                  </span>
                )}

                {isCurrent && item.href && <meta itemprop="item" content={item.href} />}
                <meta itemprop="position" content={String(index + 1)} />

                {!isCurrent && (
                  <span class={`${COMPONENT_NAME}__separator`} aria-hidden="true">
                    <dh-icon name={this.separator} inline />
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }
}
